import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const menuItems = [
    { label: 'Forside', href: '/' },
    {
      label: 'For jobsøgere',
      children: [
        { label: 'Bliv vikar', href: '/bliv-vikar' },
        { label: 'Udtalelser fra vikarer', href: '/udtalelser-fra-vikarer' }
      ]
    },
    {
      label: 'For virksomheder',
      children: [
        { label: 'Hvad tilbyder vi?', href: '/hvad-tilbyder-vi' },
        { label: 'Referencer', href: '/referencer' }
      ]
    },
    {
      label: 'Om os',
      children: [
        { label: 'Medarbejdere', href: '/medarbejdere' },
        { label: 'Mission, vision og værdier', href: '/mission-vision-og-vaerdier' },
        { label: 'Privatlivspolitik', href: '/privatlivspolitik' }
      ]
    },
    { label: 'Kontakt', href: '/kontakt' }
  ]

  const toggleDropdown = (index) => {
    if (activeDropdown === index) {
      setActiveDropdown(null)
    } else {
      setActiveDropdown(index)
    }
  }

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const isChildActive = (children) => {
    return children.some(child => location.pathname === child.href)
  }

  const isHome = location.pathname === '/'

  return (
    <header className={`z-50 w-full transition-all duration-300 ${
      isHome && !isOpen && !isScrolled
        ? 'absolute top-0 left-0 right-0 bg-transparent border-b-0' 
        : 'sticky top-0 bg-white border-b border-slate-100 shadow-sm'
    }`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center" title="Gå til forsiden · Vikar4">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=180,format=auto/assets/7b6c8b8c-dd85-4faa-a1de-b9b48f1b2725/vikar4-logo-transparent.png"
            alt="Vikar4 Vikarservice Logo"
            className="h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden min-[991px]:flex items-center gap-8">
          {menuItems.map((item, index) => {
            if (item.children) {
              const hasActiveChild = isChildActive(item.children)
              return (
                <div key={index} className="relative group">
                  <button
                    className={`flex items-center text-sm font-semibold transition-colors duration-200 py-2 ${
                      hasActiveChild ? 'text-[#015D9A]' : 'text-slate-600 hover:text-[#015D9A]'
                    }`}
                  >
                    {item.label}
                    <i className="fa-regular fa-chevron-down ml-1 text-[10px] transition-transform duration-200 group-hover:rotate-180" />
                  </button>
                  <div className="absolute left-0 top-full w-56 pt-2 origin-top-left opacity-0 scale-95 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto transition-all duration-200 z-50">
                    <div className="rounded-sm border border-slate-100 bg-white p-2 shadow-none ring-1 ring-black/5">
                      {item.children.map((child, childIdx) => (
                        <Link
                          key={childIdx}
                          to={child.href}
                          className={`block rounded-sm px-4 py-2 text-sm transition-colors duration-150 font-semibold ${
                            isActive(child.href)
                              ? 'bg-slate-100 text-[#015D9A] font-bold'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-[#015D9A]'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )
            }

            return (
              <Link
                key={index}
                to={item.href}
                className={`text-sm font-semibold transition-colors duration-200 py-2 ${
                  isActive(item.href) ? 'text-[#015D9A] font-bold' : 'text-slate-600 hover:text-[#015D9A]'
                }`}
              >
                {item.label}
              </Link>
            )
          })}
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex h-10 w-10 items-center justify-center rounded-sm border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 min-[991px]:hidden"
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-lg`} />
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      {isOpen && (
        <div className="border-t border-slate-100 bg-white px-4 py-3 shadow-none min-[991px]:hidden">
          <nav className="flex flex-col gap-1">
            {menuItems.map((item, index) => {
              if (item.children) {
                const hasActiveChild = isChildActive(item.children)
                const isDropdownOpen = activeDropdown === index
                return (
                  <div key={index} className="flex flex-col">
                    <button
                      onClick={() => toggleDropdown(index)}
                      className={`flex items-center justify-between rounded-sm px-3 py-2.5 text-base font-semibold transition-colors ${
                        hasActiveChild ? 'text-[#015D9A] bg-slate-50 font-bold' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {item.label}
                      <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDropdownOpen && (
                      <div className="ml-4 mt-1 flex flex-col gap-1 border-l-2 border-slate-100 pl-3">
                        {item.children.map((child, childIdx) => (
                          <Link
                            key={childIdx}
                            to={child.href}
                            onClick={() => setIsOpen(false)}
                            className={`rounded-sm px-3 py-2 text-sm transition-colors font-semibold ${
                              isActive(child.href)
                                ? 'text-[#015D9A] font-bold'
                                : 'text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={index}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`rounded-sm px-3 py-2.5 text-base font-semibold transition-colors ${
                    isActive(item.href)
                      ? 'bg-slate-50 text-[#015D9A] font-bold'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {item.label}
                </Link>
              )
            })}
            <div className="mt-4 border-t border-slate-100 pt-4">
              <a
                href="tel:27606027"
                className="flex items-center justify-center gap-2.5 rounded-sm bg-[#015D9A] py-3 text-center text-sm font-semibold text-white transition-all hover:bg-[#014d80]"
              >
                <i className="fa-solid fa-phone" />
                Ring til os: (+45) 27 60 60 27
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
